<template>
  <div
    ref="tooltip"
    v-clickout="hideTooltip"
    :class="['gfr-tooltip', arrowPosition]"
    @mouseenter="showTooltipHandle"
    @mouseleave="hideTooltipHandle"
    @click="toggleTooltip"
  >
    <slot />
    <div class="gfr-tooltip-wrapper" :style="contentStyle">
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="transform scale-90 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-200 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-90 opacity-0"
      >
        <div v-show="show" ref="tooltipCtx" :class="['gfr-tooltip-content', contentClass]">
          <i v-if="props.type === 'arrow'" class="gfr-tooltip-content-arrow" :class="`${arrowPlacement}`" />
          {{ message }}
        </div>
      </transition>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { rectInfo } from '~/components/gfr/utils/types'
import { getBoundingClientRect, isElementOutOfViewport } from '~/components/gfr/utils'
type placementOptions = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'bottom-center' | 'top-center'
const props = defineProps({
  message: {
    type: String,
    default: ''
  },
  trigger: {
    type: String as PropType<'click' | 'hover'>,
    default: 'hover'
  },
  placement: {
    type: String as PropType<placementOptions>,
    default: 'bottom-start'
  },
  offsetX: {
    type: Number,
    default: 0
  },
  offsetY: {
    type: Number,
    default: 4
  },
  contentClass: String,
  type: { type: String as PropType<'default' | 'arrow'>, default: 'default' }
})

const show = ref(false)
const rect = ref<null | rectInfo>(null)
const tooltip = ref<HTMLElement>()
const tooltipCtx = ref<HTMLElement>()
const ctxRect = ref<{ width: number; height: number } | null>(null)
const arrowPlacement = ref(props.placement)
// 维护arrowPosition来处理箭头位置
const arrowPosition = ref(props.placement.split('-').join(' '))
let timer: any
/* TODO 与Dropdown Menu合并逻辑代码 */
const contentStyle = computed(() => {
  if (!rect.value || !ctxRect.value) return ''
  const { height, top, left, right, width } = rect.value
  const { width: ctxWidth, height: ctxHeight } = ctxRect.value || { width: 0, height: 0 }
  const [startX, endX, topY, bottomY, centerX] = [
    left + props.offsetX,
    right - props.offsetX - ctxWidth,
    top - props.offsetY - ctxHeight,
    top + height + props.offsetY,
    left - ctxWidth / 2 + width / 2
  ]
  const variable = {
    '--tooltip-transform-x': '0px',
    '--tooltip-transform-y': '0px'
  }
  const actualPlacement = getActualPlacement(
    isElementOutOfViewport({
      x: props.placement.includes('end') ? endX : props.placement.includes('start') ? startX : centerX,
      y: props.placement.includes('top') ? topY : bottomY,
      width: ctxWidth,
      height: ctxHeight
    })
  )
  arrowPlacement.value = actualPlacement as placementOptions
  switch (actualPlacement) {
    case 'bottom-start':
      variable['--tooltip-transform-x'] = `${startX}px`
      variable['--tooltip-transform-y'] = `${bottomY}px`
      break
    case 'bottom-end':
      variable['--tooltip-transform-x'] = `${endX}px`
      variable['--tooltip-transform-y'] = `${bottomY}px`
      break
    case 'bottom-center':
      variable['--tooltip-transform-x'] = `${centerX}px`
      variable['--tooltip-transform-y'] = `${bottomY}px`
      break
    case 'top-start':
      variable['--tooltip-transform-x'] = `${startX}px`
      variable['--tooltip-transform-y'] = `${topY}px`
      break
    case 'top-end':
      variable['--tooltip-transform-x'] = `${endX}px`
      variable['--tooltip-transform-y'] = `${topY}px`
      break
    case 'top-center':
      variable['--tooltip-transform-x'] = `${centerX}px`
      variable['--tooltip-transform-y'] = `${topY}px`
      break
    default:
      break
  }
  return variable
})
const getActualPlacement = ({
  isOutside,
  directions
}: {
  isOutside: boolean
  directions: { top: boolean; right: boolean; bottom: boolean; left: boolean }
}) => {
  if (!isOutside) {
    return props.placement
  }
  let [pos, dir] = props.placement.split('-')
  pos = directions.top ? 'bottom' : pos
  pos = directions.bottom ? 'top' : pos
  if (directions.left || directions.right) {
    if (directions.left) {
      dir = 'start'
    } else if (directions.right) {
      dir = 'end'
    } else {
      dir = 'center'
    }
  }
  return `${pos}-${dir}`
}
function updateRect() {
  rect.value = getBoundingClientRect(tooltip.value as HTMLElement)
}
function showTooltipHandle() {
  if (Device().isMobile || props.trigger === 'click') return
  show.value = true
}
function hideTooltipHandle() {
  if (Device().isMobile || props.trigger === 'click') return
  show.value = false
}
function toggleTooltip() {
  show.value = !show.value
}
function hideTooltip() {
  if (Device().isMobile) show.value = false
}

async function getCtxRect() {
  await nextTick()
  return {
    width: tooltipCtx.value?.offsetWidth || 0,
    height: tooltipCtx.value?.offsetHeight || 0
  }
}
watch(
  () => show.value,
  async (val) => {
    if (val) {
      updateRect()
      !ctxRect.value && (ctxRect.value = await getCtxRect())
      window.addEventListener('resize', updateRect)
      window.addEventListener('scroll', throttle(updateRect, 10))
      if (Device().isMobile) {
        timer = setTimeout(() => {
          hideTooltip()
          clearTimeout(timer)
        }, 3000)
      }
    } else {
      window.removeEventListener('resize', updateRect)
      window.addEventListener('scroll', throttle(updateRect, 10))
      clearTimeout(timer)
    }
  }
)

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateRect)
  window.addEventListener('scroll', throttle(updateRect, 10))
})
</script>
<style scoped lang="scss">
@import './styles/index.scss';
</style>
